import { effect, untracked } from "@preact/signals";

import AllPhrasesIndex from "AllPhrasesIndex";
import BookStore from "stores/BookStore";
import BookTagsStore from "stores/BookTagsStore";
import FlashcardSessionStore from "stores/FlashcardSessionStore";
import GuideStore from "stores/GuideStore";
import PracticeStore from "stores/PracticeStore";
import RemoteUserWordsStore from "stores/RemoteUserWordsStore";
import ShelvesStore from "stores/ShelvesStore";
import StoredNotificationsStore from "stores/StoredNotificationsStore";
import StreakStore from "stores/StreakStore";
import UserStore from "stores/UserStore";

const user = new UserStore();

const remoteUserWordsFactory = () => {
  const remoteUserWords = new RemoteUserWordsStore([], { user });

  remoteUserWords.onChange((words, previousWords) => {
    const previousByID = new Map(
      previousWords.map((word) => [word.id, word.state.value])
    );
    const currentByID = new Map(
      words.map((word) => [word.id, word.state.value])
    );

    for (const word of words) {
      const current = word.state.value;
      const previous = previousByID.get(word.id);
      if (!previous && current.language && current.word) {
        stores.allPhrasesIndex.addToPhraseIndex(current.language, current.word);
      } else if (
        previous &&
        current.language &&
        current.word &&
        previous.word !== current.word
      ) {
        stores.allPhrasesIndex.removeFromPhraseIndex(
          current.language,
          previous.word
        );
        stores.allPhrasesIndex.addToPhraseIndex(current.language, current.word);
      } else if (
        previous &&
        !previous.recalledThisSession &&
        current.recalledThisSession &&
        current.language &&
        current.word
      ) {
        stores.allPhrasesIndex.wordRecalled(current.language, current.word);
      }
    }

    for (const word of previousWords) {
      const previous = word.state.value;
      if (!currentByID.has(word.id) && previous.language && previous.word) {
        stores.allPhrasesIndex.removeFromPhraseIndex(
          previous.language,
          previous.word
        );
      }
    }

    stores.practiceStore.resetGroups();
  });

  return remoteUserWords;
};

// Application stores
const stores: {
  user: UserStore;
  practiceStore: PracticeStore;
  allPhrasesIndex: AllPhrasesIndex;
  bookTags: BookTagsStore;
  guide: GuideStore;
  shelves: ShelvesStore;
  streak: StreakStore;
  storedNotifications: StoredNotificationsStore;
  remoteUserWordsFactory: () => RemoteUserWordsStore;
  remoteUserWords: RemoteUserWordsStore;
  bookModel?: BookStore;
  flashcardSession?: FlashcardSessionStore;
} = {
  user,
  practiceStore: new PracticeStore(user),
  allPhrasesIndex: new AllPhrasesIndex(),
  bookTags: new BookTagsStore(),
  guide: new GuideStore({ user }),
  shelves: new ShelvesStore(),
  streak: new StreakStore(),
  storedNotifications: new StoredNotificationsStore(),
  remoteUserWordsFactory,
  remoteUserWords: remoteUserWordsFactory(),
};

export type AppStores = typeof stores;

// Clear stored notifications (e.g. the "Don't lose your streak!" reminder)
// whenever the daily goal is met and notifications exist. This must not be
// edge-triggered on a false → true transition: when the goal is already met
// at page load, goalMet goes straight from undefined to true, and the streak
// and notification fetches can resolve in either order. The flag limits
// deletion to once per goal-met period, so a failed or slow delete can't
// retrigger itself in a loop.
const clearStoredNotificationsWhenGoalMet = ({
  streak,
  storedNotifications,
}: Pick<AppStores, "streak" | "storedNotifications">) => {
  let clearedForCurrentGoalMet = false;
  return effect(() => {
    if (!streak.todayStats?.goalMet) {
      clearedForCurrentGoalMet = false;
    } else if (
      storedNotifications.hasNotifications &&
      !clearedForCurrentGoalMet
    ) {
      clearedForCurrentGoalMet = true;
      // Use untracked to avoid subscribing to notifications.value inside
      // deleteAllStoredNotifications, which would cause an infinite loop.
      untracked(() => storedNotifications.deleteAllStoredNotifications());
    }
  });
};

clearStoredNotificationsWhenGoalMet(stores);

export const _functionsForUnitTests = { clearStoredNotificationsWhenGoalMet };

window["stores"] = stores;
export default stores;
